import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Megaphone, Loader2, Pause, Play } from "lucide-react";
import api, { getApiErrorMessage } from "../lib/api";
import { Button } from "./ui/button";
import { Carousel, CarouselContent, CarouselItem } from "./ui/carousel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const publicationDate = (item) => {
  const date = new Date(item.published_at || item.created_at);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("pt-BR");
};

function PublicationPhoto({ item, className }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [item.foto_base64]);

  return <div className={`flex min-h-0 items-center justify-center overflow-hidden bg-slate-50 ${className || ""}`}>
    {item.foto_base64 && !failed
      ? <img src={item.foto_base64} alt={item.titulo} className="h-full w-full object-contain" loading="lazy" onError={() => setFailed(true)} />
      : <Megaphone className="h-12 w-12 text-orange-300" aria-hidden="true" />}
  </div>;
}

export default function PublicationFeed() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [carouselApi, setCarouselApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [openedPublication, setOpenedPublication] = useState(null);
  const [paused, setPaused] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setAutoPlay(!reduced.matches);
    update(); reduced.addEventListener("change", update);
    return () => reduced.removeEventListener("change", update);
  }, []);
  useEffect(() => {
    if (!carouselApi || items.length < 2 || paused || openedPublication || !autoPlay) return;
    const timer = setInterval(() => { if (!document.hidden) carouselApi.canScrollNext() ? carouselApi.scrollNext() : carouselApi.scrollTo(0); }, 5000);
    return () => clearInterval(timer);
  }, [carouselApi, items.length, paused, openedPublication, autoPlay, selected]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError("");
    api.get("/publicacoes", { params: { pagina: page, limite: 10 }, signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setItems((current) => {
          const combined = page === 1 ? data.itens : [...current, ...data.itens];
          return [...new Map(combined.map((item) => [item.id, item])).values()];
        });
        setTotal(data.total);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(getApiErrorMessage(failure));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [page, retry]);

  const updateSelection = useCallback((api) => {
    setSelected(api.selectedScrollSnap());
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!carouselApi) return;
    updateSelection(carouselApi);
    carouselApi.on("select", updateSelection);
    carouselApi.on("reInit", updateSelection);
    return () => {
      carouselApi.off("select", updateSelection);
      carouselApi.off("reInit", updateSelection);
    };
  }, [carouselApi, updateSelection]);

  return <section aria-label="Publicações da loja" className="min-w-0 space-y-3" data-testid="publication-feed" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}>

    {error && <div className="flex flex-wrap items-center gap-3">
      <p role="alert" className="text-sm text-red-600">{error}</p>
      <Button variant="outline" size="sm" disabled={loading} onClick={() => setRetry((current) => current + 1)}>Tentar novamente</Button>
    </div>}
    {!items.length && loading && <div className="flex h-[320px] items-center justify-center rounded-lg border border-slate-200 bg-white sm:h-60" role="status" aria-label="Carregando publicações">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>}
    {!items.length && !loading && !error && <p className="text-sm text-slate-500">Nenhuma publicação disponível.</p>}

    {items.length > 0 && <Carousel setApi={setCarouselApi} opts={{ align: "start" }} aria-label="Publicações da loja" className="min-w-0">
      <CarouselContent viewportClassName="overflow-clip">
        {items.map((item, index) => <CarouselItem key={item.id} aria-label={`${index + 1} de ${items.length}`} aria-hidden={index !== selected} inert={index !== selected}>
          <article className="grid h-[320px] grid-rows-[160px_minmax(0,1fr)] overflow-hidden rounded-lg border border-slate-200 bg-white sm:h-60 sm:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] sm:grid-rows-1" data-testid={`publication-${item.id}`}>
            <PublicationPhoto item={item} className="h-full w-full" />
            <div className="flex min-h-0 min-w-0 flex-col gap-2 p-4 sm:p-5">
              <p className="text-xs text-slate-500">{publicationDate(item)}</p>
              <h3 className="line-clamp-1 break-words font-heading text-base font-semibold leading-5 text-slate-900">{item.titulo}</h3>
              <p className="line-clamp-1 whitespace-pre-wrap break-words text-sm leading-5 text-slate-600">{item.texto}</p>
              <Button variant="outline" size="sm" className="mt-auto w-fit shrink-0" onClick={() => setOpenedPublication(item)} aria-label={`Ver publicação: ${item.titulo}`}>Ver publicação</Button>
            </div>
          </article>
        </CarouselItem>)}
      </CarouselContent>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" disabled={!canScrollPrev} onClick={() => carouselApi?.scrollPrev()} aria-label="Publicação anterior" title="Publicação anterior">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-14 text-center text-xs tabular-nums text-slate-500" aria-live="polite" aria-atomic="true">{selected + 1} de {items.length}</span>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" disabled={!canScrollNext} onClick={() => carouselApi?.scrollNext()} aria-label="Próxima publicação" title="Próxima publicação">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {items.length > 1 && <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setAutoPlay((value) => !value)} aria-pressed={autoPlay} aria-label={autoPlay ? "Pausar publicações" : "Reproduzir publicações"} title={autoPlay ? "Pausar" : "Reproduzir"}>{autoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Button>}
        </div>
        {items.length < total && !error && <Button variant="outline" size="sm" disabled={loading} onClick={() => setPage((current) => current + 1)}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Carregar mais
        </Button>}
      </div>
    </Carousel>}

    <Dialog open={Boolean(openedPublication)} onOpenChange={(open) => !open && setOpenedPublication(null)}>
      <DialogContent className="sm:max-w-2xl" aria-describedby={undefined}>
        {openedPublication && <>
          <DialogHeader className="pr-5 text-left">
            <DialogTitle className="break-words font-heading leading-6 tracking-normal">{openedPublication.titulo}</DialogTitle>
            <p className="text-xs text-slate-500">{publicationDate(openedPublication)}</p>
          </DialogHeader>
          {openedPublication.foto_base64 && <PublicationPhoto item={openedPublication} className="h-[min(45vh,360px)] rounded-lg" />}
          <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{openedPublication.texto}</p>
        </>}
      </DialogContent>
    </Dialog>
  </section>;
}
